import { z } from 'zod';

import { OrganizationCadastralWriteFieldsSchema } from './organization-cadastral.schemas.js';

export const OrganizationPersonKindSchema = z.enum(['INDIVIDUAL', 'COMPANY']);

export type OrganizationPersonKind = z.infer<typeof OrganizationPersonKindSchema>;

const PlaceTaxRegimeSchema = z.enum(['SIMPLES_NACIONAL', 'LUCRO_PRESUMIDO', 'LUCRO_REAL']);

export const PlaceAnatomySchema = z.object({
  legalName: z.string().min(1),
  tradeName: z.string().min(1),
  documentNumber: z.string().min(11),
  stateRegistration: z.string().nullable().optional(),
  municipalRegistration: z.string().nullable().optional(),
  businessOpenedAt: z.iso.date().nullable().optional(),
  taxPayerType: z
    .enum(['SIMPLES_NACIONAL', 'LUCRO_PRESUMIDO', 'LUCRO_REAL', 'MEI', 'OTHER'])
    .nullable()
    .optional(),
  isCentralDistribution: z.boolean(),
  address: z.object({
    countryId: z.uuid(),
    stateId: z.uuid().nullable().optional(),
    cityId: z.uuid().nullable().optional(),
    stateText: z.string().nullable().optional(),
    cityText: z.string().nullable().optional(),
    zipCode: z.string().nullable().optional(),
    street: z.string().min(1),
    number: z.string().nullable().optional(),
    complement: z.string().nullable().optional(),
    neighborhood: z.string().nullable().optional(),
    reference: z.string().nullable().optional(),
  }),
});

export const ProvisionOrganizationBodySchema = z
  .object({
    name: z.string().min(1),
    slug: z
      .string()
      .min(2)
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    documentNumber: z.string().min(11),
    personKind: OrganizationPersonKindSchema.default('COMPANY'),
    ...OrganizationCadastralWriteFieldsSchema.shape,
    places: z
      .array(
        z.object({
          name: z.string().min(1),
          slug: z
            .string()
            .min(2)
            .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
          anatomy: PlaceAnatomySchema,
          taxRegime: PlaceTaxRegimeSchema.optional(),
        }),
      )
      .min(1),
    skipInvite: z.boolean().optional(),
    bootstrapAdmin: z
      .object({
        email: z.email(),
        fullName: z.string().min(1),
      })
      .optional(),
  })
  .superRefine((body, context) => {
    const digits = body.documentNumber.replace(/\D/g, '');
    if (body.personKind === 'COMPANY' && digits.length !== 14) {
      context.addIssue({
        code: 'custom',
        path: ['documentNumber'],
        message: 'CNPJ must have 14 digits',
      });
    }
    if (body.personKind === 'INDIVIDUAL' && digits.length !== 11) {
      context.addIssue({
        code: 'custom',
        path: ['documentNumber'],
        message: 'CPF must have 11 digits',
      });
    }
    if (body.personKind === 'INDIVIDUAL' && body.places.length !== 1) {
      context.addIssue({
        code: 'custom',
        path: ['places'],
        message: 'An individual organization has exactly one place',
      });
    }
    body.places.forEach((place, index) => {
      if (body.personKind === 'COMPANY' && place.taxRegime === undefined) {
        context.addIssue({
          code: 'custom',
          path: ['places', index, 'taxRegime'],
          message: 'Tax regime is required',
        });
      }
    });
  });

export type ProvisionOrganizationBody = z.infer<typeof ProvisionOrganizationBodySchema>;

export const ProvisionOrganizationResponseSchema = z.object({
  organizationId: z.uuid(),
  placeIds: z.array(z.uuid()),
  slug: z.string(),
});

export type ProvisionOrganizationResponse = z.infer<typeof ProvisionOrganizationResponseSchema>;
